import { FunctionComponent } from 'react';

import { useRouter } from 'next/router';

// Components
import ImgLazy from '@Components/Utils/Lazy/Image';

// Utils
import classnames from 'classnames';

// Libs
import { getUrl } from '@Libs/utils';
import { useHttpContext } from '@Libs/http';

// Contexts
import { useViewDetected } from '@Components/Utils/ViewDetected';
import { useAccountContext } from '@Components/Account/Contexts';

// Apis
import { getShoppingCart } from '@Libs/api/user/shoppingCart';

// Styles
import styles from './Access.module.scss';
import Anchor from '@Components/Utils/Anchor';

// Config
import host from '@Config/host';

type AccessLink = {
    icon: string;
    href: string;
    title: string;
    name?: string;
    className?: string;
    links?: any;
};

const ACCESS_LINKS: AccessLink[] = [
    {
        title: 'Hồ sơ gia đình',
        href: '/ca-nhan/ho-so-gia-dinh',
        icon: '/assets/navbar/profiles.png',
    },
    {
        name: 'shopping_cart',
        title: 'Giỏ hàng của bạn',
        href: getUrl('/gio-hang.html'),
        icon: '/assets/navbar/cart.png',
    },
    {
        title: 'Xem kết quả xét nghiệm',
        href: getUrl('/dat-hen/lich-su.html'),
        icon: '/assets/navbar/medical_test.png',
    },
    {
        title: 'Sổ địa chỉ của bạn',
        href: '/ca-nhan/dia-chi',
        icon: '/assets/navbar/addresses.png',
    },
    {
        title: 'Thông tin thanh toán',
        href: '/ca-nhan/the-thanh-toan',
        icon: '/assets/navbar/card.png',
    },
    {
        title: 'Câu hỏi của bạn',
        href: '/ca-nhan/hoi-dap',
        icon: '/assets/navbar/qa.png',
        links: ['/ca-nhan/hoi-dap', '/ca-nhan/hoi-dap/[id]'],
    },
    {
        title: 'Lịch sử giao dịch',
        href: '/ca-nhan/giao-dich',
        icon: '/assets/navbar/order.png',
    },
];

interface AccessLinksProps {
    className?: string;
    itemClassName?: string;
    linkClassName?: string;
    iconClassName?: string;
    titleClassName?: string;
    badgeClassName?: string;
    activeLink?: string;
}

export const AccessLinks: FunctionComponent<AccessLinksProps> = (props) => {
    const { pathname } = useRouter();
    const { token } = useHttpContext();
    const { signout } = useAccountContext();
    const { data: shoppingCartData } = getShoppingCart();
    const shoppingCartCount = shoppingCartData?.shoppingCart?.totalQuantityCount;

    return (
        <ul className={classnames([styles.links, props.className])}>
            {ACCESS_LINKS.map(({ title, className, href, links, icon, name }, index) => (
                <li className={classnames([styles.item, props.itemClassName])} key={`navbar_access_link_${index}`}>
                    <Anchor
                        href={href}
                        className={classnames([styles.link, className, props.linkClassName, styles.active], {
                            [props.activeLink]: links?.includes(pathname),
                        })}
                    >
                        <ImgLazy
                            src={icon}
                            className={classnames([styles.icon, props.iconClassName])}
                            width={'24px'}
                            height={'24px'}
                        />
                        <span className={classnames([styles.title, props.titleClassName])}>{title}</span>
                        {!!name && name === 'shopping_cart' && shoppingCartCount > 0 && (
                            <span className={classnames([styles.badge, props.badgeClassName])}>
                                {shoppingCartCount}
                            </span>
                        )}
                    </Anchor>
                </li>
            ))}
            {token && (
                <li className={classnames([styles.item, props.itemClassName])}>
                    <span className={classnames([styles.link, styles.logout, props.linkClassName])} onClick={signout}>
                        <ImgLazy
                            src="/assets/navbar/logout.png"
                            className={classnames([styles.icon, props.iconClassName])}
                            width={'24px'}
                            height={'24px'}
                        />
                        <span className={classnames([styles.title, props.titleClassName])}>Đăng xuất</span>
                    </span>
                </li>
            )}
        </ul>
    );
};

interface AccessProps {
    className?: string;
}

const Access: FunctionComponent<AccessProps> = (props) => {
    const { summary } = useAccountContext();
    const { deviceType } = useViewDetected();

    const profile = (summary?.consumer?.profiles || []).find(({ relation }) => relation === 'self') || {};

    const className = classnames([
        styles.wrapper,
        {
            [styles.mobile]: ['mobile', 'tablet'].includes(deviceType),
        },
        props.className,
    ]);

    return (
        <div className={className}>
            <div className={styles.body}>
                <div className={styles.improve_profile}>
                    <div className={styles.text}>
                        <h4 className={styles.title}> Hoàn thiện hồ sơ cá nhân</h4>
                        <div className={styles.description}>
                            Hoàn thiện thông tin tài khoản để nhận đầy đủ thông tin từ eDoctor
                        </div>

                        <Anchor href={`${host.homeDomain}/ca-nhan/ho-so-gia-dinh/${profile?.id}`} className={styles.link}>
                            Cập nhật ngay
                        </Anchor>
                    </div>

                    <ImgLazy
                        className={styles.image}
                        src={'/assets/navbar/improve_profile.png'}
                        width={'80px'}
                        height={'35px'}
                    />
                </div>

                <AccessLinks />
            </div>
        </div>
    );
};

Access.defaultProps = {
    className: '',
};

export default Access;
